// src/hooks/useAuth.ts
import { useState, useEffect, useCallback } from "react";
import { apiClient } from "./apiClient";
import { EventLogger } from "./eventLogger";
import { setUserData } from "../store/userData";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store";
import { CredentialsIsAuthedResponse } from "@services/credentials/types";

const AUTH_STATE_COOKIE_NAME = "dobby_auth_state";
const POLL_INTERVAL_MS = 60 * 1000;

/**
 * Checks if the auth cookie exists
 */
const checkAuthCookie = (): boolean => {
  if (typeof document === "undefined") return false; // SSR guard
  return document.cookie
    .split("; ")
    .some((row) => row.startsWith(`${AUTH_STATE_COOKIE_NAME}=`));
};

/**
 * Retrieves the user_id from the auth cookie.
 * Returns null if the cookie is not present or malformed.
 */
// const getUserIdFromCookie = (): string | null => {
//   if (typeof document === "undefined") return null; // SSR guard

//   const cookie = document.cookie
//     .split("; ")
//     .find((row) => row.startsWith(`${AUTH_STATE_COOKIE_NAME}=`));
//   if (!cookie) return null;

//   const cookieValue = cookie.split("=")[1];
//   try {
//     const parsed = JSON.parse(JSON.parse(cookieValue));
//     return parsed.user_id || null;
//   } catch {
//     return null;
//   }
// };

const fetchUserData = async () => {
  try {
    const res = await apiClient.post<CredentialsIsAuthedResponse>(
      "/credentials/is_authed"
    );
    return res;
  } catch (e) {
    console.error(e);
  }
};

export const useAuth = (
  onAuthCheckCompleted: ((auth: boolean) => void) | null = null
) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(checkAuthCookie());
  const dispatch = useDispatch<AppDispatch>();

  const checkAuth = useCallback(async (): Promise<boolean> => {
    let isAuthed = false;
    const res = await fetchUserData();
    if (res?.isAuthed) {
      isAuthed = true;
    }

    if (res?.userEmail && res.userId) {
      dispatch(
        setUserData({
          email: res.userEmail!,
          id: res.userId!,
          isAdmin: res.isAdmin!,
        })
      );
      EventLogger.getInstance().setUser(res.userId);
    }
    // const hasAuthStateCookie = checkAuthCookie();
    // if (hasAuthStateCookie) {
    //   isAuthed = true;
    //   const userId = getUserIdFromCookie();
    //   if (userId) {
    //     EventLogger.getInstance().setUser(userId);
    //   } else {
    //     console.error("no user Id found");
    //   }
    // } else {
    //   // cookie expired but let's see if we're still authed.
    //   // unfortunately I cannot check if I have a refresh token, so I'll just have to keep pinging
    //   // is_auth
    //   try {
    //     const res = await apiClient.post(
    //       "/credentials/is_authed"
    //     );
    //     isAuthed = res.data.isAuthed;
    //     dispatch(
    //       setUserData({ email: res.data.userEmail!, id: res.data.userId! })
    //     );
    //   } catch (e) {
    //     console.error(e);
    //   }
    // }

    setIsLoggedIn(isAuthed);
    if (onAuthCheckCompleted) {
      onAuthCheckCompleted(isAuthed);
    }
    return isAuthed;
  }, []);

  useEffect(() => {
    const interval = setInterval(checkAuth, POLL_INTERVAL_MS);
    checkAuth();

    return () => {
      clearInterval(interval);
    };
  }, [checkAuth]);

  return {
    isLoggedIn,
    checkAuth,
  };
};
